import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const Users = () => {
	const { isDark } = useTheme();
	const { getAuthToken } = useAuth();
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [users, setUsers] = useState([]);
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 20,
		total: 0,
		pages: 0
	});
	const [filters, setFilters] = useState({
		role: '',
		search: '',
		is_active: ''
	});
	const [formData, setFormData] = useState({
		full_name: '',
		email: '',
		password: '',
		role: 'viewer'
	});
	const [editingUser, setEditingUser] = useState(null);
	const [showForm, setShowForm] = useState(false);

	// API Base URL - should be configured in environment
	const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://rccgtr.net/api';



	useEffect(() => {
		loadUsers();
	}, [pagination.page, filters]);

	const loadUsers = async () => {
		try {
			setLoading(true);
			const token = getAuthToken();
			console.log('Auth token for loadUsers:', token);
			
			const params = new URLSearchParams({
				page: pagination.page,
				limit: pagination.limit,
				...filters
			});

			const response = await fetch(`${API_BASE_URL}/users?${params}`, {
				headers: {
					'Authorization': `Bearer ${token}`
				}
			});

			if (response.status === 401) {
				setMessage('Please log in to access user management');
				return;
			}

			const data = await response.json();
			
			if (data.success) {
				setUsers(data.data);
				setPagination(prev => ({
					...prev,
					total: data.pagination.total,
					pages: data.pagination.pages
				}));
			} else {
				setMessage('Failed to load users');
			}
		} catch (error) {
			console.error('Error loading users:', error);
			setMessage('Error loading users');
		} finally {
			setLoading(false);
		}
	};

	const handleFormSubmit = async (e) => {
		e.preventDefault();
		
		try {
			setLoading(true);
			const token = getAuthToken();
			console.log('Auth token for form submit:', token);
			
			const url = editingUser 
				? `${API_BASE_URL}/users/${editingUser.id}`
				: `${API_BASE_URL}/users`;
			
			const method = editingUser ? 'PUT' : 'POST';
			
			const response = await fetch(url, {
				method,
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${token}`
				},
				body: JSON.stringify(formData)
			});
			
			const data = await response.json();
			
			if (data.success) {
				setMessage(editingUser ? 'User updated successfully!' : 'User created successfully!');
				setShowForm(false);
				setEditingUser(null);
				setFormData({ full_name: '', email: '', password: '', role: 'Viewer' });
				loadUsers();
			} else {
				setMessage(data.error || 'Failed to save user');
			}
		} catch (error) {
			console.error('Error saving user:', error);
			setMessage('Error saving user. Please check your connection and authentication.');
		} finally {
			setLoading(false);
		}
	};

	const editUser = (user) => {
		setEditingUser(user);
		setFormData({
			full_name: user.full_name,
			email: user.email,
			password: '',
			role: user.role
		});
		setShowForm(true);
	};

	const deleteUser = async (userId) => {
		if (!window.confirm('Are you sure you want to deactivate this user?')) {
			return;
		}

		try {
			setLoading(true);
			const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
				method: 'DELETE',
				headers: {
					'Authorization': `Bearer ${getAuthToken()}`
				}
			});
			
			const data = await response.json();
			
			if (data.success) {
				setMessage('User deactivated successfully!');
				loadUsers();
			} else {
				setMessage('Failed to deactivate user');
			}
		} catch (error) {
			console.error('Error deactivating user:', error);
			setMessage('Error deactivating user');
		} finally {
			setLoading(false);
		}
	};

	const cancelEdit = () => {
		setShowForm(false);
		setEditingUser(null);
		setFormData({ full_name: '', email: '', password: '', role: 'viewer' });
	};

	const handleFilterChange = (field, value) => {
		setFilters(prev => ({ ...prev, [field]: value }));
		setPagination(prev => ({ ...prev, page: 1 }));
	};

	const handlePageChange = (newPage) => {
		setPagination(prev => ({ ...prev, page: newPage }));
	};

	const getRoleBadge = (role) => {
		const badges = {
			'Admin': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
			'Manager': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
			'Viewer': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
		};
		return badges[role] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
	};

	const getStatusBadge = (isActive) => {
		return isActive 
			? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
			: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
	};

	return (
		<Layout title="User Management">
			<div className="max-w-5xl mx-auto mt-6 space-y-6">
				{/* Message Display */}
				{message && (
					<div className={`mb-4 p-4 rounded-lg ${
						message.includes('successfully') 
							? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
							: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
					}`}>
						{message}
						<button 
							onClick={() => setMessage('')}
							className="float-right font-bold"
						>
							×
						</button>
					</div>
				)}

				{/* Create User Form */}
				{showForm && (
					<div className={`${isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'} rounded-xl shadow p-4`}>
						<p className="text-sm opacity-80 mb-3">
							{editingUser ? 'Edit existing user.' : 'Create or register users for the system.'}
						</p>
						<form onSubmit={handleFormSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<input 
								className={`${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : 'bg-white'} border p-3 rounded`} 
								placeholder="Full name" 
								value={formData.full_name}
								onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
								required
							/>
							<input 
								type="email" 
								className={`${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : 'bg-white'} border p-3 rounded`} 
								placeholder="Email" 
								value={formData.email}
								onChange={(e) => setFormData({ ...formData, email: e.target.value })}
								required
							/>
							<input 
								type="password" 
								className={`${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : 'bg-white'} border p-3 rounded`} 
								placeholder={editingUser ? 'New password (leave blank to keep current)' : 'Password'} 
								value={formData.password}
								onChange={(e) => setFormData({ ...formData, password: e.target.value })}
								required={!editingUser}
							/>
							<select 
								className={`${isDark ? 'bg-gray-900 border-gray-700 text-indigo-100' : 'bg-white'} border p-3 rounded`}
								value={formData.role}
								onChange={(e) => setFormData({ ...formData, role: e.target.value })}
								required
							>
								<option value="Viewer">Viewer</option>
								<option value="Manager">Manager</option>
								<option value="Admin">Admin</option>
							</select>
							<div className="md:col-span-2 flex gap-3">
								<button 
									type="submit" 
									className="bg-indigo-600 text-white rounded p-3 disabled:opacity-50"
									disabled={loading}
								>
									{loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
								</button>
								<button 
									type="button" 
									onClick={cancelEdit}
									className="bg-gray-600 text-white rounded p-3"
								>
									Cancel
								</button>
							</div>
						</form>
					</div>
				)}

				{/* Users List */}
				<div className={`${isDark ? 'bg-gray-800 text-indigo-100' : 'bg-white'} rounded-xl shadow p-4`}>
					<div className="flex justify-between items-center mb-4">
						<p className="font-semibold">Users</p>
						<div className="flex gap-2">
							<select 
								value={filters.role} 
								onChange={(e) => handleFilterChange('role', e.target.value)}
								className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-2 py-1`}
							>
								<option value="">All Roles</option>
								<option value="Admin">Admin</option>
								<option value="Manager">Manager</option>
								<option value="Viewer">Viewer</option>
							</select>
							<select 
								value={filters.is_active} 
								onChange={(e) => handleFilterChange('is_active', e.target.value)}
								className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-2 py-1`}
							>
								<option value="">All Status</option>
								<option value="true">Active</option>
								<option value="false">Inactive</option>
							</select>
							<input 
								placeholder="Search..." 
								value={filters.search}
								onChange={(e) => handleFilterChange('search', e.target.value)}
								className={`${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'} border rounded px-2 py-1`} 
							/>
							<button 
								onClick={() => setShowForm(true)}
								className="bg-green-600 text-white px-3 py-1 rounded text-sm"
							>
								Add User
							</button>
						</div>
					</div>
					
					{loading ? (
						<div className="flex justify-center items-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
						</div>
					) : users.length === 0 ? (
						<div className="text-center py-8 text-gray-500">
							No users found
						</div>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="min-w-full text-sm">
									<thead>
										<tr className={`${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
											<th className="p-2 text-left">SN</th>
											<th className="p-2 text-left">Name</th>
											<th className="p-2 text-left">Email</th>
											<th className="p-2 text-left">Role</th>
											<th className="p-2 text-left">Status</th>
											<th className="p-2 text-left">Last Login</th>
											<th className="p-2 text-left">Actions</th>
										</tr>
									</thead>
									<tbody>
										{users.map((user,i) => (
											<tr key={user.id} className={`${isDark ? 'border-gray-700' : 'border-gray-200'} border-b`}>
												<td className="p-2">{1 + i}</td>
												<td className="p-2">{user.full_name}</td>
												<td className="p-2">{user.email}</td>
												<td className="p-2">
													<span className={`px-2 py-1 rounded text-xs ${getRoleBadge(user.role)}`}>
														{user.role}
													</span>
												</td>
												<td className="p-2">
													<span className={`px-2 py-1 rounded text-xs ${getStatusBadge(user.is_active)}`}>
														{user.is_active ? 'Active' : 'Inactive'}
													</span>
												</td>
												<td className="p-2">
													{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
												</td>
												<td className="p-2">
													<div className="flex gap-2">
														<button
															onClick={() => editUser(user)}
															className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-sm"
														>
															Edit
														</button>
														<button
															onClick={async () => {
																if (!window.confirm(user.is_active ? 'Deactivate this user?' : 'Reactivate this user?')) return;
																try {
																	setLoading(true);
																	const token = getAuthToken();
																	const endpoint = user.is_active ? `${API_BASE_URL}/users/${user.id}` : `${API_BASE_URL}/users/${user.id}/reactivate`;
																	const method = user.is_active ? 'DELETE' : 'POST';
																	const resp = await fetch(endpoint, { method, headers: { 'Authorization': `Bearer ${token}` } });
																	const data = await resp.json();
																	if (data.success) {
																		setMessage(user.is_active ? 'User deactivated' : 'User reactivated');
																		loadUsers();
																	} else {
																		setMessage(data.error || 'Action failed');
																	}
																} catch (e) {
																	setMessage('Action failed');
																} finally { setLoading(false); }
															}}
															className="text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-300 text-sm"
														>
															{user.is_active ? 'Deactivate' : 'Reactivate'}
														</button>
														<button
															onClick={async () => {
																if (!window.confirm('Permanently delete this user?')) return;
																try {
																	setLoading(true);
																	const token = getAuthToken();
																	const resp = await fetch(`${API_BASE_URL}/users/${user.id}/hard`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
																	const data = await resp.json();
																	if (data.success) {
																		setMessage('User deleted permanently');
																		loadUsers();
																	} else {
																		setMessage(data.error || 'Delete failed');
																	}
																} catch (e) {
																	setMessage('Delete failed');
																} finally { setLoading(false); }
															}}
															className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-sm"
														>
															Delete
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							
							{/* Pagination */}
							{pagination.pages > 1 && (
								<div className="flex justify-center items-center gap-2 mt-4">
									<button
										onClick={() => handlePageChange(pagination.page - 1)}
										disabled={pagination.page === 1}
										className="px-3 py-1 rounded border disabled:opacity-50"
									>
										Previous
									</button>
									<span className="px-3 py-1">
										Page {pagination.page} of {pagination.pages}
									</span>
									<button
										onClick={() => handlePageChange(pagination.page + 1)}
										disabled={pagination.page === pagination.pages}
										className="px-3 py-1 rounded border disabled:opacity-50"
									>
										Next
									</button>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</Layout>
	);
};

export default Users;


